import FieldValidator from '@/services/fieldValidatorService';
import { nextTick } from 'vue';
import { Options, Vue } from 'vue-class-component';
import { PROPOSAL_INFO_ERROR } from '@/store/modules/ProposalContext';
import store from '@/store/store';
import VueScrollTo from 'vue-scrollto';
import { isInView } from '@/services/uiService';
import { customClickHandler } from '@/services/FieldService';
import { Log } from '@/appinsights/logging/log';
import { isDevelop } from '@/services/environmentService';
import { CmsField } from '@/types/types';
import { INPUT_TYPES } from '@/types/enums';

const fv: FieldValidator = new FieldValidator('default');
@Options({
	name: 'VerticalStepComponent',
	components: {},
	props: {
		step: Object,
		visibleError: { type: Boolean, default: false },
	},
})
export default class VerticalStepComponent extends Vue {
	public step: any;
	public visibleError: boolean;

	public renderPage = false;

	public addressValue: string = null;
	public searchAddress(evt) {
		console.warn('TODO IMPLEMENT address search ', evt?.matchText);
		if (evt?.matchText) {
			this.addressValue = evt?.matchText;
			return;
		}

		this.addressValue = undefined;
	}

	public mounted() {
		this.init();
	}

	private async init() {
		try {
			this.setVisible(true, 1500);
			this.renderPage = true;

			store.subscribeAction((action, state) => {
				if (action.type === PROPOSAL_INFO_ERROR) {
					let firstError = false;
					this.step.inputFields.forEach((field) => {
						if (!firstError && !field.isValid) {
							console.warn('first error', field.id);
							firstError = true;
							if (!isInView(`${field.uiId}`)) {
								VueScrollTo.scrollTo(`#${field.uiId}`, 500, { easing: 'ease-out', offset: -20 });
							}
						}
					});
				}
			});
		} catch (error) {
			Log.exception('Error Proposal field subscription', error, {
				quoteId: store.state.ProposalContext.quote_id,
			});
		}
	}

	private setVisible(focusNext = false, focusDelay = 300): void {
		let focusId: string;
		let isAllValid = true;

		let foundNotValidField = false;
		this.step.inputFields.forEach((field: CmsField, inx) => {
			if (field.hidden) {
				return;
			}
			if (field.showIfApiValue && (field.value === undefined || field.value === null || field.value === '')) {
				// hide and ignore field
				field.isValid = true;
				field.isVisible = false;
				return;
			}
			if (foundNotValidField) {
				if (field.contentType !== 'TextOnlyField') {
					return;
				}
			}

			field.isVisible = true;
			if (isAllValid && !field.isValid) {
				focusId = field.uiId;
			}

			// Dependency check
			this.handleDependency(field);

			if (!field.isValid) {
				foundNotValidField = true;
				isAllValid = false;
				if (isDevelop) {
					console.warn('field is not valid', field.id);
				}
			}
		});
		this.step.isValid = isAllValid;

		if (focusNext && focusId) {
			setTimeout(() => {
				const input = document.getElementById(focusId);
				if (input) {
					document.getElementById(focusId).focus();
				}
			}, focusDelay);
		}
	}

	private handleDependency(field: CmsField) {
		if (field.dependentId) {
			// check if field is dependent on another field (and value)
			let other = this.step.inputFields.find((other) => other.id === field.dependentId);
			let otherValue = other?.value;

			if (!other) {
				// check if field is dependent on a product property
				otherValue = store.state.ProposalContext.productProperties.get(field.dependentId);
				if (otherValue) {
					other = {
						isValid: true, // this is only to make subsequent if statement work
					};
				}
			}

			if (other?.isValid || (other?.isVisible && otherValue !== undefined && otherValue !== '')) {
				if (!field.dependentValue || otherValue + '' === field.dependentValue) {
					field.isVisible = true;
					this.updateValid(field);
				} else {
					field.isVisible = false;
					this.updateValid(field);
					// set field to valid, since it shouldn't stop the flow if it's dependent value isn't set.
					field.isValid = true;
				}
			} else {
				field.isVisible = false;
				this.updateValid(field);
				// set field to valid, since it shouldn't stop the flow if it's dependent value isn't set.
				field.isValid = true;
			}
		}
	}

	private updateValid(field) {
		switch (field.contentType) {
			case INPUT_TYPES.TextField:
				this.inputValidate(field);
				break;
			case INPUT_TYPES.RadioButtonField:
				this.clickValidate(field, { value: field.value });
				break;
			case INPUT_TYPES.DatePickerField:
				this.dateValidate(field, field.value);
				break;
			case INPUT_TYPES.CheckField:
				this.checkboxValidate(field, field.value);
				break;
			case INPUT_TYPES.TextSearchField:
				this.searchValidate(field, { matchText: field.value });
				break;
			default:
				return;
		}
	}

	private inputValidate(field) {
		if (!field?.value || field?.value?.trim() === '') {
			field.isValid = false;
			return;
		}
		if (field.min !== 0 || field.max !== 0) {
			if (field.type === 'number') {
				const v = parseInt(field.value);
				field.isValid = v >= field.min && (v <= field.max || field.max === 0);
				return;
			}
			// Text
			field.isValid = field.value.length >= field.min && (field.max === 0 || field.value.length <= field.max);
			return;
		}
		field.isValid = fv.isValid(field.value);
	}

	public inputChange(field) {
		this.inputValidate(field);
		this.setVisible(false);
	}

	private clickValidate(field, evt) {
		if (fv.isValid(evt?.value)) {
			field.value = evt.value;
			field.isValid = true;
		} else {
			field.isValid = false;
		}
		customClickHandler(this.step.id, field.id, evt?.value);
	}
	public radioClick(field, evt) {
		this.clickValidate(field, evt);
		this.setVisible(true);
	}

	private checkboxValidate(field, evt) {
		// KLK look into this mess!!! console.log('checkboxValidate', field, evt);
		field.isValid = true; // always valid
		if (evt === undefined || evt === null) {
			return;
		}
		let safeEvt;
		if (typeof evt === 'boolean') {
			safeEvt = {
				checked: evt,
			};
		} else {
			safeEvt = {
				checked: evt?.checked ?? false,
			};
		}
		if (safeEvt.checked === field.value) {
			return;
		}
		field.value = safeEvt.checked;
		if (evt.translatedValue) {
			field.value = evt.translatedValue;
		}
		if (this.step.hasMasterChildCheckFields) {
			this.handleMasterChildren(field);
		}

		if (!field.value && field.isRequired) {
			field.isValid = false;
		}
	}

	private handleMasterChildren(field) {
		// special handling for checkField children
		let updateUi = false;
		this.step.inputFields.forEach((f) => {
			if (f.masterId === field.id) {
				f.value = field.value;
				updateUi = true;
			}
		});
		if (updateUi) {
			this.renderPage = !this.renderPage;
			nextTick(() => {
				this.renderPage = !this.renderPage;
			});
		}
		// special handling for checkField master
		if (field.masterId) {
			this.step.inputFields.forEach((f) => {
				if (f.id === field.masterId) {
					if (f.value) {
						f.value = false;
						this.renderPage = !this.renderPage;
						nextTick(() => {
							this.renderPage = !this.renderPage;
						});
					}
				}
			});
		}
	}

	public checkboxEvent(field, evt) {
		this.checkboxValidate(field, evt);
		this.setVisible(true);
	}

	private dateValidate(field, value) {
		if (value) {
			field.value = value;
			field.isValid = true;
		} else {
			field.value = undefined;
			field.isValid = false;
		}
	}
	public dateSelected(field, value) {
		this.dateValidate(field, value);
		this.setVisible(field.isValid);
	}

	private searchValidate(field, evt) {
		if (evt?.matchText) {
			let inx = field.labels.indexOf(evt.matchText);
			if (inx > -1) {
				const id = field.options[inx];
				field.value = id;
				field.isValid = true;
				return;
			}
			inx = field.options.indexOf(evt.matchText);
			if (inx > -1) {
				field.value = evt.matchText;
				field.isValid = true;
				return;
			}
		}
		field.value = undefined;
		field.isValid = false;
	}

	public searchSelected(field, evt) {
		this.searchValidate(field, evt);
		this.setVisible(field.isValid);
	}
}
